import { motion } from "framer-motion";
import { Reveal } from "./Reveal";

const DANCER_IMG =
  "https://images.unsplash.com/photo-1730440983959-8d4bf2afe5f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxDYW1ib2RpYW4lMjBjbGFzc2ljYWwlMjBraG1lciUyMGRhbmNlcnxlbnwwfHx8fDE3OTEzMTI4NTh8MA&ixlib=rb-4.1.0&q=85";
const RELIEF_IMG =
  "https://images.unsplash.com/photo-1660830930759-b87784fc8476?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxBbmdrb3IlMjBXYXQlMjBhcHNhcmElMjBkZXZhdGElMjBzdG9uZSUyMGNhcnZpbmd8ZW58MHx8fHwxNzkxMzEyODU4fDA&ixlib=rb-4.1.0&q=85";

const TIMELINE = [
  ["2000s", "Fieldwork begins at Angkor Wat — cataloging nearly 2,000 devata portraits one by one."],
  ["2009", "Launch of Devata.org, the first open archive dedicated to the goddesses of Angkor."],
  ["2013–17", "DatAsia Press publishes rare Khmer histories, translations and travel classics."],
  ["Today", "A combined archive preserving Cambodia's sacred art, history and literature for the next generation."],
];

export const AboutArchive = () => {
  return (
    <section
      id="about"
      data-testid="about-archive-section"
      className="relative py-24 lg:py-36 scroll-mt-20"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5 relative">
          <Reveal>
            <div className="relative overflow-hidden rounded-sm deckle aspect-[4/5]">
              <img
                src={DANCER_IMG}
                alt="Cambodian classical dancer"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: -6 }}
            whileInView={{ opacity: 1, scale: 1, rotate: -4 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="absolute -bottom-8 -right-4 w-36 h-44 lg:w-44 lg:h-52 overflow-hidden rounded-sm border-4 border-[var(--bg)] deckle hidden sm:block"
          >
            <img
              src={RELIEF_IMG}
              alt="Devata bas-relief"
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </motion.div>
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-[0.28em] text-[var(--gold)]">
              The Archive &amp; Its Mission
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[var(--text)]">
              Preserving a thousand years of Khmer memory
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-6 font-sans text-base sm:text-lg leading-relaxed text-[var(--text-2)]">
              Devata.org began as a quiet obsession: to truly see the women carved
              into the walls of Angkor Wat. DatAsia grew alongside it — a small press
              rescuing the histories, chronicles and travelogues that tell Cambodia's
              story. Together they form one living archive.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <blockquote className="mt-6 font-hand text-2xl sm:text-3xl text-[var(--terracotta)] leading-snug">
              “Angkor Wat alone requires years of study — living with understanding.”
            </blockquote>
          </Reveal>

          <div className="mt-10 space-y-0">
            {TIMELINE.map(([year, text], i) => (
              <Reveal key={year} delay={0.1 + i * 0.06}>
                <div
                  data-testid="archive-timeline-item"
                  className="grid grid-cols-[88px_1fr] gap-5 py-5 border-t border-[var(--border-c)]"
                >
                  <span className="font-mono text-xs uppercase tracking-[0.15em] text-[var(--gold)] pt-1">
                    {year}
                  </span>
                  <p className="font-sans text-sm leading-relaxed text-[var(--text-2)]">
                    {text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
